import { buttonBaseClasses } from '@mui/material/ButtonBase';
import { inputBaseClasses } from '@mui/material/InputBase';
import { linkClasses } from '@mui/material/Link';
import { Theme } from '@mui/material/styles';

import { focusRing } from '../../css';

// ----------------------------------------------------------------------

/** Room around a tab for its focus ring — tabs have no horizontal padding of their own. */
const TAB_RING_GAP = 6;

/**
 * Every keyboard-focus indicator in the app. Components never draw their own focus outline:
 *
 * - MUI controls (Button, IconButton, Chip, Tab, MenuItem, Checkbox, Switch, …) all render a
 *   ButtonBase, which flags keyboard focus with `.Mui-focusVisible`.
 * - Everything else made focusable by hand (`tabIndex={0}` rows, dnd handles, dropzones, plain
 *   anchors) gets the global `:focus-visible` rule. It is wrapped in `:where()` so it has zero
 *   specificity and any component style wins without `!important`. Text fields are excluded —
 *   they already show focus through their border — and so are `tabindex="-1"` containers that
 *   only receive programmatic focus (a dialog paper, `<main>` after the skip link).
 * - Full-bleed items inside clipping containers draw the ring inset, or it would be cut off.
 */
export function focus(theme: Theme) {
  const ring = focusRing(theme);
  const insetRing = focusRing(theme, 'inset');
  const insetWhenFocusVisible = { [`&.${buttonBaseClasses.focusVisible}`]: insetRing };

  const textEntry = `input, textarea, select, [contenteditable], .${inputBaseClasses.input}`;

  // Padding plus an equal negative margin: room for the ring to overflow into, nothing moves.
  const tabRingRoom = {
    marginLeft: -TAB_RING_GAP,
    marginRight: -TAB_RING_GAP,
    paddingLeft: TAB_RING_GAP,
    paddingRight: TAB_RING_GAP,
  };

  return {
    MuiCssBaseline: {
      styleOverrides: {
        [`:where(:focus-visible:not(${textEntry}, [tabindex="-1"]))`]: ring,
        // Containers that only take programmatic focus aren't controls — the browser's own ring
        // would outline the whole page or dialog. Still zero specificity: a roving-tabindex
        // control (`tabindex="-1"` until it is the active one) keeps its component ring.
        ':where([tabindex="-1"]:focus-visible)': { outline: 'none' },
        // simplebar's scrollable region is focusable (keyboard scrolling) and fills a clipped box.
        '.simplebar-content-wrapper:focus-visible': insetRing,
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: { [`&.${buttonBaseClasses.focusVisible}`]: ring },
      },
    },
    // MUI Link sets `outline: auto` (the browser default) — use the app's ring instead.
    MuiLink: {
      styleOverrides: {
        root: { [`&.${linkClasses.focusVisible}`]: ring },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { '&:focus-visible': insetRing },
      },
    },
    MuiListItemButton: { styleOverrides: { root: insetWhenFocusVisible } },
    MuiMenuItem: { styleOverrides: { root: insetWhenFocusVisible } },
    // Tab bars scroll, so they clip, and a tab's label runs edge to edge: neither an outset nor
    // an inset outline fits. The ring is drawn on `::after` instead — inset vertically, outset
    // horizontally into the room the tab bar makes for it.
    MuiTabs: {
      styleOverrides: { root: tabRingRoom, scroller: tabRingRoom },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          overflow: 'visible',
          [`&.${buttonBaseClasses.focusVisible}`]: {
            outline: 'none',
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: `${TAB_RING_GAP}px -${TAB_RING_GAP}px`,
              border: ring.outline,
              borderRadius: theme.shape.borderRadius,
              pointerEvents: 'none',
            },
          },
        },
      },
    },
    MuiCardActionArea: { styleOverrides: { root: insetWhenFocusVisible } },
    MuiAccordionSummary: { styleOverrides: { root: insetWhenFocusVisible } },
  };
}
